import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Package, Users, Search, ArrowLeft, Eye } from "lucide-react";
import { customerService } from "@/lib/database";
import type { Customer, Shipment } from "@/lib/supabase";

interface SearchResults {
  customers: Customer[];
  shipments: Shipment[];
}

export default function AdminSearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResults>({
    customers: [],
    shipments: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get initial search data from navigation state
    if (location.state?.query && location.state?.results) {
      setSearchQuery(location.state.query);
      setSearchResults(location.state.results);
    }
  }, [location.state]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const results = await customerService.searchCustomersAndShipments(
        searchQuery.trim()
      );
      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Utility function to calculate cubic feet from dimensions string
  function calculateCubicFeet(dimensions?: string): string {
    if (!dimensions) return "0";

    const parts = dimensions.split("x").map((d) => parseFloat(d.trim()));
    if (parts.length !== 3 || parts.some(isNaN)) return "0";

    const [length, width, height] = parts;
    const cubicFeet = (length * width * height) / 1728; // Convert cubic inches to cubic feet
    return cubicFeet.toFixed(2);
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      picked_up_by_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      delivered_to_recipient:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      in_transit_to_country:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      in_transit_to_recipient:
        "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      ready_for_pickup:
        "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200",
      arrived_at_warehouse:
        "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200",
      processing_at_warehouse:
        "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
      picking_up_from_sender:
        "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200",
      received_at_warehouse:
        "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200",
      created: "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
      estimated:
        "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
      confirmed:
        "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
      refunded: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
        }
      >
        {status.replace(new RegExp("_", "g"), " ").toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Search Results</h1>
          <p className="text-gray-600">
            {searchQuery
              ? `Results for "${searchQuery}"`
              : "Search customers and shipments"}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
          <CardDescription>
            Search for customers by name/email or shipments by tracking number
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Enter customer name, email, or tracking number..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? "Searching..." : "Search"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {(searchResults.customers.length > 0 ||
        searchResults.shipments.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customers Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Customers ({searchResults.customers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {searchResults.customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Users className="h-5 w-5 text-gray-400" />

                      <div>
                        <p className="font-medium text-gray-900">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {customer.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                      <div className="flex justify-between space-x-6">
                        {customer.phone ? (
                          <div className="md:text-center">
                            <p className="text-sm font-medium text-gray-900">
                              Phone
                            </p>
                            <p className="text-xs text-gray-600">
                              {customer.phone}
                            </p>
                          </div>
                        ) : (
                          <div className="text-start md:text-center">
                            <p className="text-sm font-medium text-gray-900">
                              Phone
                            </p>
                            <p className="text-gray-500 text-xs">
                              No phone provided
                            </p>
                          </div>
                        )}

                        {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Language</p>
                      <p className="text-xs text-gray-600 capitalize">{customer.preferred_language}</p>
                    </div> */}

                        {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Tier</p>
                      <Badge className={customer.customer_tier === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                        {customer.customer_tier}
                      </Badge>
                    </div> */}

                        {/* <div className="md:text-center">
                      <p className="text-sm font-medium text-gray-900">Status</p>
                      <Badge className={customer.account_status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 hover:bg-red-100 text-red-800'}>
                        {customer.account_status}
                      </Badge>
                    </div> */}

                        <div className="text-right md:text-center">
                          <p className="text-sm font-medium text-gray-900">
                            Date Joined
                          </p>
                          <p className="text-xs text-gray-600">
                            {new Date(customer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        onClick={() => {
                          navigate(`/admin/customers/${customer.id}`);
                        }}
                        className="mt-4 md:mt-0"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {/* <Button variant="ghost" size="sm">
                        <Edit
                          className="h-4 w-4"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setIsCustomerModalOpen(true);
                          }}
                        />
                      </Button> */}
                    </div>
                  </div>
                ))}

                {searchResults.customers.length === 0 && searchQuery && (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No customers found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shipments Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Shipments ({searchResults.shipments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {searchResults.shipments.map((shipment) => (
                  <div
                    key={shipment.id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Package className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {shipment.tracking_number}
                        </p>
                        <p className="text-sm text-gray-600">
                          {shipment.customer?.first_name}{" "}
                          {shipment.customer?.last_name}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                      <div className="flex justify-between space-x-6">
                        <div className="md:text-center">
                          <p className="text-sm font-medium text-gray-900">
                            Method
                          </p>
                          <p className="text-xs text-gray-600 capitalize">
                            {shipment.shipping_method}
                          </p>
                        </div>

                        <div className="text-right md:text-center">
                          <p className="text-sm font-medium text-gray-900">
                            {shipment.shipping_method === "sea"
                              ? "Volume"
                              : "Weight"}
                          </p>
                          <p className="text-xs text-gray-600">
                            {shipment.shipping_method === "sea"
                              ? `${calculateCubicFeet(shipment.dimensions)} cft`
                              : `${shipment.weight} lbs`}
                          </p>
                        </div>
                      </div>

                      <div className="flex mt-2 md:mt-0 justify-between space-x-6">
                        <div className="md:text-center">
                          <p className="text-sm font-medium text-gray-900">
                            Cost
                          </p>
                          <p className="text-xs text-gray-600">
                            ${shipment.total_cost}
                          </p>
                        </div>

                        <div className="text-right md:text-center">
                          <p className="text-sm font-medium text-gray-900">
                            Created
                          </p>
                          <p className="text-xs text-gray-600">
                            {new Date(shipment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-center my-4 md:my-0">
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(shipment.status)}
                        </div>
                      </div>

                      <Button
                        size="sm"
                        onClick={() => {
                          navigate(
                            `/admin/shipments/${shipment.tracking_number}`
                          );
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {/* <Button variant="ghost" size="sm">
                                            <Edit 
                                              className="h-4 w-4" 
                                              onClick={() => {
                                                setSelectedShipment(shipment);
                                                setIsShipmentModalOpen(true);
                                              }}
                                            />
                                          </Button> */}
                    </div>
                  </div>
                ))}

                {searchResults.shipments.length === 0 && searchQuery && (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No shipments found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Results */}
      {searchQuery &&
        searchResults.customers.length === 0 &&
        searchResults.shipments.length === 0 &&
        !loading && (
          <Card>
            <CardContent className="text-center py-12">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No results found
              </h3>
              <p className="text-gray-600">
                Try searching with different keywords or check the spelling.
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
